import React, { useState } from 'react';
import Card from '../../components/Card/Card';
import DefinitionList from '../../components/DefinitionList/DefinitionList';
import ErrorMessageNewFund from '../../components/ErrorMessages/ErrorMessageNewFund';
import ErrorMessageNoData from '../../components/ErrorMessages/ErrorMessageNoData';
import GqlQueryWrapper from '../../components/GqlQueryWrapper/GqlQueryWrapper';
import Table from '../../components/Table/Table';
import TablePreRender from '../../components/Table/Table.prerender';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import Toggle from '../../components/Toggle/Toggle';
import { CARD_NAMES } from '../../utils/constants';
import {
    isContractualETF,
    quarterEndBeforeInception
} from '../../utils/fund-info';
import styles from './ETFAverageAnnualTable.css';
import { ETF_AVERAGE_ANNUAL_TOTAL_RETURNS } from './queries';
import { etfAverageAnnualTableCardUtil } from './util';

const ETFAverageAnnualTableCard = ({ ticker }) => {
    const [selectedPeriod, setSelectedPeriod] = useState('monthly');
    const buttonList = [
        { label: 'Monthly', val: 'monthly' },
        { label: 'Quarterly', val: 'quarterly' }
    ];

    return (
        <div className="section-container">
            <Card addOverflow>
                <div className="subsection-no-border">
                    <GqlQueryWrapper
                        query={ETF_AVERAGE_ANNUAL_TOTAL_RETURNS}
                        variables={{ ticker }}
                        // @todo extract titles from the table component
                        // so we don't have to include this conditional
                        // logic in the util
                        dataFunc={etfAverageAnnualTableCardUtil(selectedPeriod)}
                        placeholder={() => <TablePreRender />}
                    >
                        {({
                            data: {
                                grossExpenseRatio,
                                netExpenseRatio,
                                primaryBenchmarkName,
                                inceptionDate,
                                asOfDate,
                                quarterlyEndDate,
                                hasExtraordinaryPerformance,
                                ...data
                            }
                        }) => (
                            <React.Fragment>
                                {/* can't display data if fund is incepted after recent quarter end; ref: US212176 */}
                                {!quarterlyEndDate ? (
                                    <ErrorMessageNewFund
                                        cardName={
                                            CARD_NAMES.ETF_AVERAGE_ANNUAL_CHART
                                        }
                                    />
                                ) : (
                                    data && (
                                        <React.Fragment>
                                            <div className="title-group">
                                                <TitleWithDescription title="Average Annual Returns" />
                                                <div className="buttons">
                                                    <Toggle
                                                        data={buttonList}
                                                        selected={
                                                            selectedPeriod
                                                        }
                                                        toggle={
                                                            setSelectedPeriod
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <div>
                                                    <strong>
                                                        Expense Ratio
                                                    </strong>
                                                </div>
                                                <div className="additional-data">
                                                    <div>
                                                        <DefinitionList
                                                            theme="inline"
                                                            data={[
                                                                [
                                                                    'Gross',
                                                                    grossExpenseRatio
                                                                ],
                                                                [
                                                                    'Net',
                                                                    netExpenseRatio
                                                                ]
                                                            ]}
                                                        />
                                                    </div>
                                                    <div>
                                                        <DefinitionList
                                                            theme="inline"
                                                            data={[
                                                                [
                                                                    'Benchmark',
                                                                    primaryBenchmarkName
                                                                ],
                                                                [
                                                                    'Inception Date',
                                                                    inceptionDate
                                                                ]
                                                            ]}
                                                        />
                                                    </div>
                                                    <div className="as-of">
                                                        As of {asOfDate}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="table-wrapper">
                                                <Table
                                                    data={data}
                                                    displayTooltip
                                                />
                                            </div>
                                            {isContractualETF(ticker) && (
                                                <div className="disclaimer disclaimer-large">
                                                    The expense waiver type is
                                                    Contractual. Expense
                                                    Limitation does not expire
                                                    as T. Rowe Price Associates,
                                                    Inc., is required to
                                                    permanently waive a portion
                                                    of its management fee. See
                                                    prospectus for more details
                                                    on expense waiver.
                                                </div>
                                            )}
                                            {hasExtraordinaryPerformance && (
                                                <div className="disclaimer disclaimer-bold">
                                                    <sup>†</sup> Investors
                                                    should note that the fund’s
                                                    short-term performance is
                                                    highly unusual and unlikely
                                                    to be sustained.{' '}
                                                </div>
                                            )}
                                            <div className="disclaimer disclaimer-bold">
                                                Performance data quoted
                                                represents past performance and
                                                does not guarantee future
                                                results; current performance may
                                                be higher or lower than
                                                performance quoted. Investment
                                                returns and principal value will
                                                fluctuate and shares, when sold,
                                                may be worth more or less than
                                                their original cost. Market
                                                returns are based on the
                                                midpoint of the bid/ask spread
                                                as of 4p.m. ET and do not
                                                represent returns an investor
                                                would receive if shares were
                                                traded at other times.
                                            </div>
                                            <div className="disclaimer">
                                                Returns for periods less than
                                                one year are not annualized. For
                                                funds less than one year old,
                                                the Since Inception return
                                                figure is not annualized and
                                                represents an aggregate total
                                                return.
                                            </div>
                                        </React.Fragment>
                                    )
                                )}
                                {!quarterEndBeforeInception(inceptionDate) &&
                                    !data && (
                                        <ErrorMessageNoData
                                            cardName={
                                                CARD_NAMES.ETF_AVERAGE_ANNUAL_CHART
                                            }
                                        />
                                    )}
                            </React.Fragment>
                        )}
                    </GqlQueryWrapper>
                </div>
            </Card>
            <style jsx>{styles}</style>
        </div>
    );
};

export default ETFAverageAnnualTableCard;
